import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputBase,
  IconButton,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Heading from '../../Components/Heading/heading.tsx';
import Dropdown from '../../Components/DropDown/drop-down.tsx';
import AddClientModal from './add-client-modal.tsx';
import { getModels, initiateBilling } from './action.ts';
import { toast } from 'react-toastify';

interface AddBillingModalProps {
  open: boolean;
  onClose: () => void;
  billedClientOptions: { id: string; label: string; uuid: string }[];
  onSuccess: () => void;
  getClients: () => void;
}

interface TableRowData {
  id: number;
  model?: string;
  servicePeriod?: string;
  rate: number;
  quantity: string;
  discount: string;
  quantityError?: string;
  discountError?: string;
  modelError?: string;
  servicePeriodError?: string;
}

const AddBillingModal: React.FC<AddBillingModalProps> = ({ open, onClose, billedClientOptions, onSuccess, getClients }) => {
  const [client, setClient] = useState<string>('');
  const [clientError, setClientError] = useState<string>('');
  const [type, setType] = useState<string>('device_type');
  const [isAddClientOpen, setIsAddClientOpen] = useState<boolean>(false);
  const [allModelsData, setAllModelsData] = useState<{ devices: any[], subscriptions: any[] }>({ devices: [], subscriptions: [] });

  const getEmptyRow = (): TableRowData => ({ 
    id: Date.now() + Math.random(),
    model: '', servicePeriod: '', rate: 0, quantity: '', discount: '', quantityError: '', discountError: '', modelError: '', servicePeriodError: '' 
  });

  const [deviceRows, setDeviceRows] = useState<TableRowData[]>([getEmptyRow()]);
  const [subscriptionRows, setSubscriptionRows] = useState<TableRowData[]>([getEmptyRow()]);

  useEffect(() => {
    if (open || !isAddClientOpen) {
      fetchModelsList();
    }
  }, [open, isAddClientOpen]);

  const handleClientChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    if (value === 'add_new') {
      setIsAddClientOpen(true);
    } else {
      setClient(value);
      if (value) setClientError('');
    }
  };

  const fetchModelsList = () => {
    if(open) {
      getModels(
        (response: any) => {
          if (response?.data) {
            setAllModelsData({
              devices: response.data.devices || [],
              subscriptions: response.data.subscriptions || []
            });
          }
        },
        (error: any) => console.error("Failed to fetch models", error)
      );
    }
  };

  const modalClientOptions = [
    ...billedClientOptions.filter((opt) => opt.id !== '' && opt.id !== 'ALL').sort((a, b) => a.label.localeCompare(b.label)),
    { id: 'add_new', label: 'Add New +' },
  ];

  const handleTypeChange = (event: SelectChangeEvent<string>) => {
    setType(event.target.value);
    setDeviceRows([getEmptyRow()]);
    setSubscriptionRows([getEmptyRow()]);
  };

  const handleAddRow = (category: 'device' | 'subscription') => {
    if (category === 'device') setDeviceRows([...deviceRows, getEmptyRow()]);
    else setSubscriptionRows([...subscriptionRows, getEmptyRow()]);
  };

  const handleRemoveRow = (category: 'device' | 'subscription', id: number) => {
    if (category === 'device' && deviceRows.length > 1) {
      setDeviceRows(deviceRows.filter((row) => row.id !== id));
    } else if (category === 'subscription' && subscriptionRows.length > 1) {
      setSubscriptionRows(subscriptionRows.filter((row) => row.id !== id));
    }
  };

  const handleRowChange = (category: 'device' | 'subscription', id: number, field: keyof TableRowData, value: string) => {
    const isSub = category === 'subscription';
    const dataList = isSub ? allModelsData.subscriptions : allModelsData.devices;

    const rowUpdater = (prevRows: TableRowData[]) => 
      prevRows.map((row) => {
        if (row.id !== id) return row;
        const updatedRow = { ...row, [field]: value };

        if (field === 'model') {
          updatedRow.modelError = '';
          const selectedModelInfo = dataList.find((item: any) => String(item.model) === value);
          updatedRow.rate = selectedModelInfo?.selling_price ? Number(selectedModelInfo.selling_price) : 0;
        }

        if (field === 'servicePeriod') {
          if (value === '') updatedRow.servicePeriodError = '';
          else if (!/^\d+$/.test(value)) updatedRow.servicePeriodError = 'Numbers only';
          else {
            const numValue = parseInt(value, 10);
            updatedRow.servicePeriodError = (numValue < 6 || numValue > 18) ? 'Must be 6-18' : '';
          }
        }

        if (field === 'quantity') {
          if (value === '') updatedRow.quantityError = '';
          else if (value.includes('.') || /[a-zA-Z]/.test(value) || /[^0-9-]/.test(value)) updatedRow.quantityError = 'Invalid Input';
          else if (value.lastIndexOf('-') > 0) updatedRow.quantityError = 'Invalid format';
          else updatedRow.quantityError = '';
        }

        if (field === 'discount') {
          if (value === '') updatedRow.discountError = '';
          else if (/[a-zA-Z]/.test(value) || /[^0-9.]/.test(value)) updatedRow.discountError = 'Invalid Input';
          else {
            const numValue = parseFloat(value);
            if (numValue < 0 || numValue > 40) updatedRow.discountError = 'Must be 0-40';
            else updatedRow.discountError = '';
          }
        }

        return updatedRow;
      });

    if (isSub) setSubscriptionRows(rowUpdater);
    else setDeviceRows(rowUpdater);
  };

  const handleClear = () => {
    setClient('');
    setClientError('');
    setType('device_type');
    setDeviceRows([getEmptyRow()]);
    setSubscriptionRows([getEmptyRow()]);
  };

  const handleClose = () => {
    handleClear();
    onClose();
  };

  const validateRows = (rows: TableRowData[], category: 'device' | 'subscription') => {
    let isValid = true;
    const isSub = category === 'subscription';

    const validated = rows.map((row) => {
      let qErr = row.quantityError;
      let dErr = row.discountError;
      let mErr = row.modelError;
      let sErr = row.servicePeriodError;

      if (!row.quantity) qErr = 'Required';
      if (!row.model || row.model.trim() === '') mErr = 'Required';
      
      if (row.discount) {
        const numDiscount = parseFloat(row.discount);
        if (numDiscount < 0 || numDiscount > 40) dErr = 'Must be 0-40';
      }

      if (isSub) {
        if (!row.servicePeriod || row.servicePeriod.trim() === '') {
          sErr = 'Required';
        } else {
          const numValue = parseInt(row.servicePeriod, 10);
          if (isNaN(numValue) || numValue < 6 || numValue > 18) sErr = 'Must be 6-18';
        }
      }

      if (qErr || dErr || mErr || (isSub && sErr)) isValid = false;

      return { ...row, quantityError: qErr, discountError: dErr, modelError: mErr, servicePeriodError: sErr };
    });

    return { isValid, validated };
  };

  const buildPayload = (rows: TableRowData[], category: 'device' | 'subscription') => {
    const isSub = category === 'subscription';
    const dataList = isSub ? allModelsData.subscriptions : allModelsData.devices;

    return rows.map((row) => {
      const serviceUuid = dataList.find((item) => String(item.model) === row.model)?.uuid || '';
      
      const qty = Number(row.quantity) || 0;
      const rate = row.rate || 0;
      const discountPercentage = Number(row.discount) || 0;
      const discountAmount = (rate * qty * discountPercentage) / 100;

      const payload: any = {
        services_id: serviceUuid,
        count_devices: qty,
        discount: discountAmount,
      };

      if (isSub) payload.service_period = row.servicePeriod;

      return payload;
    });
  };

 const handleAdd = () => {
    const clientUuid = billedClientOptions.find((opt) => opt.id === client)?.uuid || '';
    if (!clientUuid) {
      setClientError('Client is required');
      return;
    }
    setClientError('');

    let allValid = true;
    let finalPayload: any[] = [];

    if (type === 'device_type') {

      const isSubscriptionEmpty = subscriptionRows.every(row => 
        (!row.model || row.model.trim() === '') && 
        (!row.quantity || String(row.quantity).trim() === '') &&
        (!row.servicePeriod || String(row.servicePeriod).trim() === '')
      );

      if (isSubscriptionEmpty) {
        toast.error('Subscription type is mandatory');
        return; 
      }

      const deviceValidation = validateRows(deviceRows, 'device');
      setDeviceRows(deviceValidation.validated);
      if (!deviceValidation.isValid) allValid = false;

      const subValidation = validateRows(subscriptionRows, 'subscription');
      setSubscriptionRows(subValidation.validated);
      if (!subValidation.isValid) allValid = false;

      if (allValid) {
        finalPayload = [
          ...buildPayload(deviceValidation.validated, 'device'),
          ...buildPayload(subValidation.validated, 'subscription')
        ];
      }
    } else {
      const subValidation = validateRows(subscriptionRows, 'subscription');
      setSubscriptionRows(subValidation.validated);
      if (!subValidation.isValid) allValid = false;

      if (allValid) {
        finalPayload = buildPayload(subValidation.validated, 'subscription');
      }
    }

    if (!allValid) return;

    initiateBilling(
      clientUuid,
      finalPayload,
      (response: any) => {
        toast.success(response?.message || 'Billing initiated successfully');
        onSuccess();
        handleClose();
      },
      (error: any) => toast.error(error?.message || 'Failed to initiate billing')
    );
  };
  const renderTableSection = (category: 'device' | 'subscription', rows: TableRowData[]) => {
    const isSub = category === 'subscription';
    const dataList = isSub ? allModelsData.subscriptions : allModelsData.devices;
    const dropdownOptions = dataList.map((item: any) => ({ id: String(item.model), label: String(item.model) }));

    return (
      <Box mt={isSub && type === 'device_type' ? 4 : 0}>
        {type === 'device_type' && (
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#4b5563' }}>
            {isSub ? 'Subscription Billing' : 'Device Billing'}
          </Typography>
        )}
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 1, boxShadow: 'none' }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: '#f9fafb' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: '#4b5563', width: '85px', minWidth: '85px' }}>Model</TableCell>
                {isSub && <TableCell align="left" sx={{ fontWeight: 600, color: '#4b5563', width: '70px', minWidth: '70px', borderLeft: '1px solid #e0e0e0' }}>Period</TableCell>}
                <TableCell align="left" sx={{ fontWeight: 600, color: '#4b5563', width: '70px', minWidth: '70px', borderLeft: '1px solid #e0e0e0' }}>Rate (₹)</TableCell>
                <TableCell align="left" sx={{ fontWeight: 600, color: '#4b5563', width: '70px', minWidth: '70px', borderLeft: '1px solid #e0e0e0' }}>Quantity</TableCell>
                <TableCell align="left" sx={{ fontWeight: 600, color: '#4b5563', width: '75px', minWidth: '75px', borderLeft: '1px solid #e0e0e0' }}>Discount (%)</TableCell>
                <TableCell sx={{ width: '50px', minWidth: '50px', borderLeft: '1px solid #e0e0e0' }} align="center"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { borderBottom: 0 } }}>
                  <TableCell sx={{ p: 0, verticalAlign: 'top' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', px: 1, pt: 0.5, pb: row.modelError ? 0 : 0.5 }}>
                      <Box sx={{ border: row.modelError ? '1px solid #d32f2f' : '1px solid transparent', borderRadius: '4px' }}>
                        <Dropdown
                          options={dropdownOptions}
                          selectedValue={row.model || ''}
                          placeholder="Select model"
                          handleChange={(e: { target: { value: string; }; }) => handleRowChange(category, row.id, 'model', e.target.value)}
                          width="180px" /* Changed to 100% to fit the responsive column */
                          height="32px"
                        />
                      </Box>
                      {row.modelError && (
                        <Typography color="error" sx={{ fontSize: '10px', px: 0.5, pt: 0.5, pb: 0.5, textAlign: 'left', lineHeight: 1.2 }}>
                          {row.modelError}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  {isSub && (
                    <TableCell sx={{ p: 0, borderLeft: '1px solid #e0e0e0', verticalAlign: 'top' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', px: 1, pt: 1, pb: row.servicePeriodError ? 0 : 1 }}>
                        <InputBase
                          type="text"
                          placeholder="months"
                          value={row.servicePeriod}
                          onChange={(e) => handleRowChange(category, row.id, 'servicePeriod', e.target.value)}
                          sx={{ width: '180px', px: 1, py: 0.5, border: row.servicePeriodError ? '1px solid #d32f2f' : '1px solid transparent', borderRadius: '4px' }}
                        />
                        {row.servicePeriodError && (
                          <Typography color="error" sx={{ fontSize: '10px', px: 1, pt: 0.5, pb: 0.5, textAlign: 'left', lineHeight: 1.2 }}>
                            {row.servicePeriodError}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  )}
                  
                  <TableCell sx={{ p: 0, borderLeft: '1px solid #e0e0e0', verticalAlign: 'top' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', px: 2, pt: 1.5 }}>
                      <Typography sx={{ fontSize: '0.875rem', color: '#374151' }}>
                        {row.rate ? row.rate.toFixed(2) : '0.00'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ p: 0, borderLeft: '1px solid #e0e0e0', verticalAlign: 'top' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', px: 1, pt: 1, pb: row.quantityError ? 0 : 1 }}>
                      <InputBase
                        type="text"
                        placeholder="0"
                        value={row.quantity}
                        onChange={(e) => handleRowChange(category, row.id, 'quantity', e.target.value)}
                        sx={{ width: '100%', px: 1, py: 0.5, border: row.quantityError ? '1px solid #d32f2f' : '1px solid transparent', borderRadius: '4px' }}
                        inputProps={{ style: { textAlign: 'right' } }}
                      />
                      {row.quantityError && (
                        <Typography color="error" sx={{ fontSize: '10px', px: 1, pt: 0.5, pb: 0.5, textAlign: 'right', lineHeight: 1.2 }}>
                          {row.quantityError}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell sx={{ p: 0, borderLeft: '1px solid #e0e0e0', verticalAlign: 'top' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', px: 1, pt: 1, pb: row.discountError ? 0 : 1 }}>
                      <InputBase
                        type="text"
                        placeholder="0"
                        value={row.discount}
                        onChange={(e) => handleRowChange(category, row.id, 'discount', e.target.value)}
                        sx={{ width: '100%', px: 1, py: 0.5, border: row.discountError ? '1px solid #d32f2f' : '1px solid transparent', borderRadius: '4px' }}
                        inputProps={{ style: { textAlign: 'right' } }}
                      />
                      {row.discountError && (
                        <Typography color="error" sx={{ fontSize: '10px', px: 1, pt: 0.5, pb: 0.5, textAlign: 'right', lineHeight: 1.2 }}>
                          {row.discountError}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell sx={{ p: 0, borderLeft: '1px solid #e0e0e0', verticalAlign: 'top' }} align="center">
                    <Box sx={{ pt: 1.5 }}>
                      <IconButton
                        onClick={() => handleRemoveRow(category, row.id)}
                        disabled={rows.length === 1}
                        color="error"
                        size="small"
                        sx={{ opacity: rows.length === 1 ? 0.3 : 0.8, '&:hover': { opacity: 1 } }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box display="flex" justifyContent="flex-end" mt={1} px={1}>
          <Typography
            onClick={() => handleAddRow(category)}
            sx={{ color: 'primary.main', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            + Add {isSub ? 'Subscription' : 'Device'} Row
          </Typography>
        </Box>
      </Box>
    );
  };

  const calculateTotalAmount = (rows: TableRowData[]) => rows.reduce((sum, row) => {
    const qty = Number(row.quantity) || 0;
    const baseAmount = (row.rate || 0) * qty;
    return sum + (baseAmount - ((baseAmount * (Number(row.discount) || 0)) / 100));
  }, 0);

  const grandTotal = (type === 'device_type' ? calculateTotalAmount(deviceRows) : 0) + calculateTotalAmount(subscriptionRows);

 return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Box sx={{ marginLeft: { xs: '0px', sm: '-180px' } }}>
        <Heading title="Add Billing" onClose={handleClose} closeIcon="true" />
      </Box>

      <DialogContent sx={{ padding: '24px' }}>
        <Box display="flex" gap={3} mb={3} mt={1}>
          <Box position="relative" flex={1}>
            <Box sx={{ border: clientError ? '1px solid #d32f2f' : '1px solid transparent', borderRadius: '4px', width: 'max-content'}}>
              <Dropdown
                options={modalClientOptions}
                selectedValue={client}
                placeholder="Select Client"
                label="Select Client"
                handleChange={handleClientChange}
                width="180px"
                height="40px"
                searchable={true}
              />
            </Box>
            {clientError && <Typography color="error" sx={{ fontSize: '12px', mt: 0.5, position: 'absolute' }}>{clientError}</Typography>}
          </Box>
          <Box position="relative" flex={1}>
            <Dropdown
              options={[
                { id: 'device_type', label: 'Device Type' },
                { id: 'subscription', label: 'Subscription Type' },
              ]}
              selectedValue={type}
              handleChange={handleTypeChange}
              width="180px"
              height="40px"
              label="Choose type"
            />
          </Box>
        </Box>

        {type === 'device_type' && renderTableSection('device', deviceRows)}
        {renderTableSection('subscription', subscriptionRows)}

        <Box display="flex" justifyContent="space-between" alignItems="center" mt={3} pt={2} borderTop="1px solid #e0e0e0">
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827', fontSize: '1.1rem' }}>
            Grand Total: ₹{grandTotal.toFixed(2)}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions style={{ justifyContent: 'center', paddingBottom: '24px' }}>
        <Button onClick={handleClear} className="custom-cancel-button" style={{ marginRight: '10px' }}>
          Clear
        </Button>
        <Button onClick={handleAdd} className="custom-button" style={{ marginLeft: '10px' }}>
          Add
        </Button>
      </DialogActions>

      <AddClientModal 
        open={isAddClientOpen} 
        onClose={() => {
          setIsAddClientOpen(false);
          getClients();
        }} 
      />
    </Dialog>
  );
};

export default AddBillingModal;
